import React from 'react';
import { Separator } from "@/components/ui/separator";
import Logo from "@/assets/the-wellness-way-logo.png"; // Use png version as webp not available
import { Phone, Mail, MapPin, Instagram, Facebook, Star, Heart, Leaf, Users } from 'lucide-react';
import { NewsletterSignup } from './NewsletterSignup';
import { BOOKING_URL } from "@/constants/urls";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const handleGoogleReview = () => {
    // Google Business review link
    window.open('https://g.co/kgs/S8BqMWh', '_blank');
  };

  return (
    <footer className="bg-sage-800 text-white py-12 relative z-10 pb-24 lg:pb-12">
      <div className="container mx-auto px-4 md:px-6">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-3 gap-10 items-start">
          {/* Brand/About & Contact */}
          <div>
            <div className="flex items-center mb-4">
              <img src={Logo} alt="The Way Wellness Hub" className="h-14 w-auto mr-4 rounded-lg shadow-md bg-white p-1" />
              <div>
                <h3 className="text-2xl font-serif font-bold mb-1">Women and The Way</h3>
                <p className="text-sage-100 text-sm mb-1">Empowering Women's Wellness</p>
              </div>
            </div>
            <p className="text-sage-100 mb-4 max-w-xs">
              Acupuncture & Chinese Medicine support for women in Currimundi and across the Sunshine Coast.
            </p>
            <div className="space-y-2 mb-6">
              <div className="flex items-center text-sage-100">
                <MapPin className="w-4 h-4 mr-2 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/zFvqUgEad6CiBjnh8"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open address in Google Maps"
                  className="text-sm hover:text-white transition-colors underline"
                >
                  700 Nicklin Way, Currimundi
                </a>
              </div>
              <div className="flex items-center text-sage-100">
                <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                <a href="tel:0415537784" className="text-sm hover:text-white transition-colors">0415 537 784</a>
              </div>
              <div className="flex items-center text-sage-100">
                <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                <a href="mailto:womenandtheway@gmail.com" className="text-sm hover:text-white transition-colors">womenandtheway@gmail.com</a>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-2">
              <a 
                href="https://www.instagram.com/women_and_the_way" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center text-sage-700 hover:bg-sage-200 transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.facebook.com/Womenandtheway" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center text-sage-700 hover:bg-sage-200 transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-serif font-bold mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li><a href="/" className="text-sage-100 hover:text-white transition-colors">Home</a></li>
              <li><a href="/#services" className="text-sage-100 hover:text-white transition-colors">Services</a></li>
              <li><a href="/#about" className="text-sage-100 hover:text-white transition-colors">About</a></li>
              <li><a href="/#contact" className="text-sage-100 hover:text-white transition-colors">Contact</a></li>
              <li><a href="/blog" className="text-sage-100 hover:text-white transition-colors">Blog</a></li>
              <li><a href="/care-packages/skin-package" className="text-sage-100 hover:text-white transition-colors">Skin Package</a></li>
              <li><a href="/online-consultations" className="text-sage-100 hover:text-white transition-colors">Online Consultations</a></li>
              <li><a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="text-sage-100 hover:text-white transition-colors">Bookings</a></li>
            </ul>
          </div>

          {/* Stay Connected (Newsletter) */}
          <div className="bg-sage-700/40 rounded-2xl p-8 flex flex-col items-center justify-center shadow-lg">
            <h3 className="text-xl font-serif font-bold mb-4">Stay Connected</h3>
            <NewsletterSignup variant="footer" />
            {/* Social icons (duplicated for mobile, but keep as per instruction) */}
            <div className="flex items-center space-x-4 mt-6">
              <a 
                href="https://www.instagram.com/women_and_the_way" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center text-sage-700 hover:bg-sage-200 transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://www.facebook.com/Womenandtheway" 
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center text-sage-700 hover:bg-sage-200 transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Newsletter/Review/Wellness Hub Sections (unchanged) */}
        <div className="mt-12" />

        {/* Separator */}
        <Separator className="my-8 bg-sage-700" />

        {/* Acknowledgment Section */}
        <div className="text-center text-sage-300 text-sm mb-8">
          <p>
            Women and The Way acknowledges the Traditional Owners of the land on which we operate and pays respect to Elders past, present, and emerging.
          </p>
        </div>

        {/* Side-by-Side Sections */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Review Suggestion Section */}
          <div className="bg-sage-700/50 rounded-lg p-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-3">
                <span className="text-2xl mr-2">🌿</span>
                <h3 className="text-lg font-medium text-white">Loved your visit?</h3>
              </div>
              <p className="text-sage-100 text-sm mb-4">
                We'd love to hear about your experience at Women and the Way. Your feedback helps others find a safe, supportive space for care.
              </p>
              <p className="text-sage-200 text-xs mb-4">
                💬 Share a review about our space, service, or your visit here:
              </p>
              <button
                onClick={handleGoogleReview}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Star className="w-4 h-4 mr-2" />
                Leave a Google Review
              </button>
              <p className="text-sage-300 text-xs mt-3 italic">
                Please avoid mentioning specific treatments or health outcomes to help us stay compliant with AHPRA guidelines.
              </p>
            </div>
          </div>

          {/* Practitioner & Space Rental Section */}
          <div className="bg-sage-700/30 rounded-lg p-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-white mb-3 flex items-center justify-center">
                <Users className="w-5 h-5 mr-2" />
                Join Our Wellness Community
              </h3>
              <p className="text-sage-100 text-sm mb-4">
                Interested in becoming a practitioner or renting space in our clinic? 
                Discover opportunities at The Way Wellness Hub.
              </p>
              <a 
                href="https://thewaywellness.com.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-sage-600 to-sage-700 hover:from-sage-500 hover:to-sage-600 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg mb-4"
              >
                <Leaf className="w-4 h-4 mr-2" />
                Explore The Way Wellness Hub
              </a>
              <div className="mt-4">
                <a href="https://thewaywellness.com.au" target="_blank" rel="noopener noreferrer">
                  <img src={Logo} alt="The Way Wellness Hub" className="mx-auto h-16" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom Section */}
        <div className="mt-4 text-center">
          <p className="text-sage-200 text-sm">
            © {currentYear} Women and The Way. All rights reserved.
          </p>
          <div className="flex justify-center space-x-6 text-xs text-sage-300 mt-2">
            <a href="/terms-conditions" className="hover:text-white transition-colors">Terms & Conditions</a>
            <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/medical-disclaimer" className="hover:text-white transition-colors">Medical Disclaimer</a>
          </div>
          <div className="mt-2 text-xs text-sage-400">
            Website by <a href="https://whizbangpop.com.au" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">WhizBangPop</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
